import { Fragment } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import { PILL_RADIUS_FILL } from "../../../constants";
import { THEME_COLOR } from "../../../styles/colors";

// TODO(MEETEX-299408): Delete this V1 rollback component after Who's Who V2 rollout.

/**
 * A card carries exactly one legacy insight. Career signals render as a pill; relationship signals
 * render as a sentence. The backend preserves the original priority in the singular insight field.
 */

// Insight kinds as sent by WhosWhoService — career signals render as a pill, relationship
// signals as a sentence. Kept here as one map so the string literals aren't repeated per switch.
const INSIGHT_KIND = {
  NEW_AMAZONIAN: "new-amazonian",
  RECENTLY_PROMOTED: "recently-promoted",
  NEW_ROLE: "new-role",
  NEW_TEAM: "new-team",
  ANNIVERSARY: "anniversary",
  NETWORK_CONNECTION: "network-connection",
  MUTUAL_COLLEAGUES: "mutual-colleagues",
  SHARED_LOCATION: "shared-location",
  SHARED_REPORTING: "shared-reporting",
};

const CAREER_KINDS = [
  INSIGHT_KIND.NEW_AMAZONIAN,
  INSIGHT_KIND.RECENTLY_PROMOTED,
  INSIGHT_KIND.NEW_ROLE,
  INSIGHT_KIND.NEW_TEAM,
  INSIGHT_KIND.ANNIVERSARY,
];

const careerLabel = (insight) => {
  switch (insight.kind) {
    case INSIGHT_KIND.NEW_AMAZONIAN:
      return "🌱 New Amazonian";
    case INSIGHT_KIND.RECENTLY_PROMOTED:
      return "⭐ Recently promoted";
    case INSIGHT_KIND.NEW_ROLE:
      return "🌱 New role";
    case INSIGHT_KIND.NEW_TEAM:
      return "🌱 New team";
    case INSIGHT_KIND.ANNIVERSARY:
      return `🎉 ${insight.years}-year Amazonian`;
    default:
      return null;
  }
};

// Filled pill, no border or dot — matches the YOU badge's Box + radius-token treatment.
const InsightBadge = ({ insight }) => {
  const label = insight && careerLabel(insight);
  if (!label) return null;

  return (
    <Theme tokens={{ boxBorderRadiusFill: PILL_RADIUS_FILL }}>
      <Box type="fill" backgroundColor={THEME_COLOR.MIST_GRAY} spacingInset="none 300">
        <Text type="b100" color="secondary"><strong>{label}</strong></Text>
      </Box>
    </Theme>
  );
};

// Inline emphasis for names/places inside a relationship sentence; bold via the strong tag.
const Emphasis = ({ children }) => <strong>{children}</strong>;

const RelationshipLine = ({ relationship }) => {
  if (!relationship) return null;

  const names = relationship.people || [];

  const joinEmphasised = (values, separator) =>
    values.map((value, i) => (
      <Fragment key={value}>
        {i > 0 && separator}
        <Emphasis>{value}</Emphasis>
      </Fragment>
    ));

  let content = null;
  switch (relationship.kind) {
    case INSIGHT_KIND.NETWORK_CONNECTION:
      if (names.length === 0) return null;
      content = <>Connected through {joinEmphasised(names, " → ")}</>;
      break;
    case INSIGHT_KIND.MUTUAL_COLLEAGUES:
      if (names.length === 0) return null;
      content = <>You both know {joinEmphasised(names, ", ")}</>;
      break;
    case INSIGHT_KIND.SHARED_LOCATION:
      if (!relationship.location) return null;
      content = <>Both based in <Emphasis>{relationship.location}</Emphasis></>;
      break;
    case INSIGHT_KIND.SHARED_REPORTING:
      if (!relationship.managerName) return null;
      content = (
        <>
          You both reported to <Emphasis>{relationship.managerName}</Emphasis>
          {relationship.managerRole ? ` · ${relationship.managerRole}` : ""}
        </>
      );
      break;
    default:
      return null;
  }

  return <Text type="b100" color="secondary">{content}</Text>;
};

const InsightSlot = ({ insight }) => {
  if (!insight) return null;
  return CAREER_KINDS.includes(insight.kind) ? (
    <Row spacing="none">
      <InsightBadge insight={insight} />
    </Row>
  ) : (
    <RelationshipLine relationship={insight} />
  );
};

export default InsightSlot;
